import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';

export default function LegalPage() {
  const navigate = useNavigate();
  const { t } = useI18n();

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <div className="container mx-auto py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={handleBackToHome}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors mb-4"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('legal.backToHome')}
          </button>
          
          <h1 className="text-3xl font-display font-bold text-slate-800 mb-2">
            {t('legal.title')}
          </h1>
          <p className="text-slate-600 text-sm">
            {t('legal.subtitle')}
          </p>
          <p className="text-slate-500 text-xs mt-1">
            {t('legal.lastUpdated')}
          </p>
        </div>

        {/* Content */}
        <div className="space-y-8">

          {/* About */}
          <section className="card-elegant p-8">
            <h2 className="text-xl font-display font-semibold text-slate-800 mb-4">
              {t('legal.about.heading')}
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-700 leading-relaxed mb-3">
                {t('legal.about.body1')}
              </p>
              <p className="text-slate-700 leading-relaxed">
                {t('legal.about.body2')}
              </p>
            </div>
          </section>

          {/* Terms of Service */}
          <section className="card-elegant p-8">
            <h2 className="text-xl font-display font-semibold text-slate-800 mb-4">
              {t('legal.terms.heading')}
            </h2>
            <div className="space-y-4 text-slate-700">
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.terms.acceptance.title')}</h3>
                <p className="leading-relaxed">{t('legal.terms.acceptance.body')}</p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.terms.license.title')}</h3>
                <p className="leading-relaxed">{t('legal.terms.license.body')}</p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.terms.account.title')}</h3>
                <p className="leading-relaxed mb-2">{t('legal.terms.account.body')}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{t('legal.terms.account.item1')}</li>
                  <li>{t('legal.terms.account.item2')}</li>
                  <li>{t('legal.terms.account.item3')}</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.terms.gallery.title')}</h3>
                <p className="leading-relaxed mb-2">{t('legal.terms.gallery.body')}</p>
                <ul className="list-disc pl-6 space-y-1 mb-2">
                  <li>{t('legal.terms.gallery.item1')}</li>
                  <li>{t('legal.terms.gallery.item2')}</li>
                  <li>{t('legal.terms.gallery.item3')}</li>
                  <li>{t('legal.terms.gallery.item4')}</li>
                </ul>
                <p className="leading-relaxed text-sm text-slate-600">{t('legal.terms.gallery.username')}</p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.terms.prohibited.title')}</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{t('legal.terms.prohibited.item1')}</li>
                  <li>{t('legal.terms.prohibited.item2')}</li>
                  <li>{t('legal.terms.prohibited.item3')}</li>
                  <li>{t('legal.terms.prohibited.item4')}</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.terms.termination.title')}</h3>
                <p className="leading-relaxed">{t('legal.terms.termination.body')}</p>
              </div>
            </div>
          </section>

          {/* Privacy Policy */}
          <section className="card-elegant p-8">
            <h2 className="text-xl font-display font-semibold text-slate-800 mb-4">
              {t('legal.privacy.heading')}
            </h2>
            <div className="space-y-4 text-slate-700">
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.privacy.dataController.title')}</h3>
                <p className="leading-relaxed">{t('legal.privacy.dataController.body')}</p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.privacy.dataCollection.title')}</h3>
                <p className="leading-relaxed mb-2">{t('legal.privacy.dataCollection.body')}</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>{t('legal.privacy.dataCollection.local.title')}:</strong> {t('legal.privacy.dataCollection.local.body')}</li>
                  <li><strong>{t('legal.privacy.dataCollection.images.title')}:</strong> {t('legal.privacy.dataCollection.images.body')}</li>
                  <li><strong>{t('legal.privacy.dataCollection.google.title')}:</strong> {t('legal.privacy.dataCollection.google.body')}</li>
                  <li><strong>{t('legal.privacy.dataCollection.backupKeys.title')}:</strong> {t('legal.privacy.dataCollection.backupKeys.body')}</li>
                  <li><strong>{t('legal.privacy.dataCollection.server.title')}:</strong> {t('legal.privacy.dataCollection.server.body')}</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.privacy.legalBasis.title')}</h3>
                <p className="leading-relaxed">{t('legal.privacy.legalBasis.body')}</p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.privacy.dataStorage.title')}</h3>
                <p className="leading-relaxed mb-2">{t('legal.privacy.dataStorage.body')}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{t('legal.privacy.dataStorage.item1')}</li>
                  <li>{t('legal.privacy.dataStorage.item2')}</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.privacy.thirdParty.title')}</h3>
                <p className="leading-relaxed mb-2">{t('legal.privacy.thirdParty.body')}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Firebase (Google Cloud):</strong> {t('legal.privacy.thirdParty.firebase')}</li>
                  <li><strong>AWS:</strong> {t('legal.privacy.thirdParty.aws')}</li>
                  <li><strong>Simple Analytics:</strong> {t('legal.privacy.thirdParty.simpleAnalytics')}</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.privacy.yourRights.title')}</h3>
                <p className="leading-relaxed mb-2">{t('legal.privacy.yourRights.body')}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>{t('legal.privacy.yourRights.access')}:</strong> {t('legal.privacy.yourRights.accessBody')}</li>
                  <li><strong>{t('legal.privacy.yourRights.deletion')}:</strong> {t('legal.privacy.yourRights.deletionBody')}</li>
                  <li><strong>{t('legal.privacy.yourRights.export')}:</strong> {t('legal.privacy.yourRights.exportBody')}</li>
                  <li><strong>{t('legal.privacy.yourRights.withdraw')}:</strong> {t('legal.privacy.yourRights.withdrawBody')}</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.privacy.cookies.title')}</h3>
                <p className="leading-relaxed">{t('legal.privacy.cookies.body')}</p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.privacy.changes.title')}</h3>
                <p className="leading-relaxed">{t('legal.privacy.changes.body')}</p>
              </div>
            </div>
          </section>

          {/* Disclaimer */}
          <section className="card-elegant p-8">
            <h2 className="text-xl font-display font-semibold text-slate-800 mb-4">
              {t('legal.disclaimer.heading')}
            </h2>
            <div className="space-y-4 text-slate-700">
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.disclaimer.service.title')}</h3>
                <p className="leading-relaxed">{t('legal.disclaimer.service.body')}</p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.disclaimer.accuracy.title')}</h3>
                <p className="leading-relaxed">{t('legal.disclaimer.accuracy.body')}</p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.disclaimer.brands.title')}</h3>
                <p className="leading-relaxed">{t('legal.disclaimer.brands.body')}</p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">{t('legal.disclaimer.liability.title')}</h3>
                <p className="leading-relaxed">{t('legal.disclaimer.liability.body')}</p>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="card-elegant p-8">
            <h2 className="text-xl font-display font-semibold text-slate-800 mb-4 flex items-center gap-2">
              {t('legal.contact.heading')}
            </h2>
            <div className="space-y-3 text-slate-700">
              <p className="leading-relaxed">
                {t('legal.contact.intro')}
              </p>
              
              <div className="">
                <div className="flex items-center gap-3">
                  <a 
                    href="mailto:markersync@gmail.com" 
                    className="underline hover:text-blue-600"
                  >
                    markersync@gmail.com
                  </a>
                </div>
                
                <div className="flex items-center gap-3 mt-2">
                  <a 
                    href="https://www.tiktok.com/@markersync_"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-600 hover:text-slate-800 transition-colors"
                  >
                    @markersync_ (TikTok)
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
